import { useRef } from "react";

export interface FileInfo {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
}

export function FileUpload({
  children,
  onFilesUpload: onFilesSelected,
  acceptedFileTypes,
  multiple,
}: {
  children: (
    onClick: (e: React.MouseEvent<HTMLElement>) => void,
  ) => React.ReactNode;
  onFilesUpload: (files: File[]) => Promise<FileInfo[]> | null;
  acceptedFileTypes?: FileType[];
  /** Allow user to select multiple files for uploading at once. */
  multiple?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  if (!onFilesSelected) {
    return children(() => {});
  }

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    inputRef.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await onFilesSelected(Array.from(e.target.files ?? []));

    e.target.value = "";
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={acceptedFileTypes?.join(",")}
        onChange={handleChange}
        style={{ display: "none" }}
      />
      {children(onClick)}
    </>
  );
}

export type FileType =
  // Documents
  | "application/pdf"
  | "application/msword"
  | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  | "application/vnd.ms-excel"
  | "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  | "application/vnd.ms-powerpoint"
  | "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  // Images
  | "image/jpeg"
  | "image/png"
  | "image/gif"
  | "image/webp"
  | "image/svg+xml"
  | "image/bmp"
  | "image/tiff"
  | "image/x-icon"
  // Audio
  | "audio/mpeg"
  | "audio/wav"
  | "audio/ogg"
  | "audio/aac"
  | "audio/webm"
  // Video
  | "video/mp4"
  | "video/mpeg"
  | "video/quicktime"
  | "video/x-msvideo"
  | "video/webm"
  // Text
  | "text/plain"
  | "text/html"
  | "text/css"
  | "text/javascript"
  | "text/csv"
  | "text/xml"
  // Archives
  | "application/zip"
  | "application/x-rar-compressed"
  | "application/x-tar"
  | "application/gzip"
  // Application
  | "application/json"
  | "application/xml"
  | "application/octet-stream";
